import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { fmtHeight } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const tagCount = (p) => p.attributes_by_season?.["2025"]?.tags?.length || 0;

const Profile = ({ p, label, testId }) => (
  <Link to={`/players/${p.id}`} data-testid={testId} className="block rounded-lg border border-slate-800 bg-[#0B0F19] p-3 hover:border-slate-600">
    <div className="eyebrow text-[10px]">{label}</div>
    <div className="mt-1 font-semibold text-white">{p.name}{p.jersey_number ? ` · #${p.jersey_number}` : ""}</div>
    <div className="font-data text-[11px] text-slate-400">
      {[p.team, p.position, p.class_year, p.height_in && fmtHeight(p.height_in)].filter(Boolean).join(" · ")}
    </div>
    <div className="mt-1 font-data text-[11px] text-slate-500">
      {p.on_current_roster === false ? "Not on current roster" : "On current roster"} · {tagCount(p)} attribute tags · {p.id}
    </div>
  </Link>
);

export const DuplicatesPanel = ({ sport, px, refreshKey }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/admin/${sport}/duplicates`).then((r) => setItems(r.data.items)), [sport]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const act = async (d, action) => {
    try {
      await api.post(`/admin/${sport}/duplicates/${d.id}/${action}`);
      toast.success(action === "merge" ? `Merged into the original ${d.existing.name} profile` : "Kept as separate profiles");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!items.length) return null;
  return (
    <div data-testid={`${px}-duplicates-panel`}>
      <h3 className="mb-1 font-display text-xl font-extrabold uppercase">Possible duplicates</h3>
      <p className="mb-3 text-sm text-slate-400">A re-scrape created a new profile that may be the same player as an existing profile with attributes. Merge keeps the original profile and its _id and updates its roster details.</p>
      <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
        {items.map((d) => (
          <div key={d.id} data-testid={`${px}-duplicate-${d.id}`} className="grid gap-3 p-4 md:grid-cols-[1fr_1fr_auto] md:items-center">
            <Profile p={d.existing} label="Original (kept on merge)" testId={`${px}-duplicate-existing-${d.id}`} />
            <Profile p={d.new} label={`New from re-scrape · ${d.reason}`} testId={`${px}-duplicate-new-${d.id}`} />
            <div className="flex gap-2">
              <button data-testid={`${px}-duplicate-merge-${d.id}`} onClick={() => act(d, "merge")}
                className="rounded-full bg-[#FF2D78] px-4 py-2 text-sm font-semibold text-white hover:bg-[#E01F66]">Merge</button>
              <button data-testid={`${px}-duplicate-keep-${d.id}`} onClick={() => act(d, "keep")}
                className="rounded-full border border-slate-700 px-4 py-2 text-sm hover:border-white">Keep separate</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
